import { useEffect, useRef, useState } from "react";
import type { InboundMessage, Realtime } from "ably";
import { LightShowLook } from "@max/common/event";
import { useAuth } from "../UserContext";
import {
  isLightShowDebug,
  lightShowDebug,
  recordAblyLog,
  recordArrival,
  recordDebug,
} from "./debug";

/**
 * A show runner's change to the live look (ENG-2291), as Backstage publishes
 * it on the event's Ably channel `lightshow:{eventId}` (message "live").
 */
export interface LiveChange {
  look: LightShowLook | null;
  /**
   * When the change was made: when Ably received it (the message
   * timestamp), the same moment for every phone.
   */
  startedAtMs: number;
  startDelayMs: number;
}

/** The message body; `startedAtMs` only if the publisher set its own. */
interface LiveMessage {
  look: LightShowLook | null;
  startDelayMs: number;
  startedAtMs?: number;
}

const isLiveMessage = (data: unknown): data is LiveMessage =>
  typeof data === "object" &&
  data !== null &&
  "look" in data &&
  typeof (data as LiveMessage).startDelayMs === "number";

/**
 * Follows the event's Ably channel, where each live change is pushed over a
 * WebSocket (long polling where those are blocked). Uses the full Ably client
 * rather than the modular one: about 20 KB more, but it can log, and debug
 * sessions run the same client fans do. On connect it replays the channel's
 * last message, so a phone that opens mid-show gets the live look at once;
 * that needs the "lightshow" namespace to persist the last message, or the
 * replay only covers the last 2 minutes. The Ably client loads only for
 * events with a light show. Tokens come from the show page's `/ably/token`
 * function, tied to the phone's Firebase uid (anonymous or signed in, the
 * same id Sentry reports carry); it connects once the phone has signed in.
 */
export const useAblyLightShow = (
  eventId: string | undefined,
  enabled: boolean,
  clockOffsetMs: number,
) => {
  const [change, setChange] = useState<LiveChange>();
  const clientId = useAuth().user?.uid;
  // Read by the subscription for debug timings, without reconnecting when
  // the clock resyncs.
  const clockOffsetRef = useRef(clockOffsetMs);
  clockOffsetRef.current = clockOffsetMs;

  useEffect(() => {
    if (!enabled || !eventId || !clientId) return;
    let cancelled = false;
    let client: Realtime | undefined;
    recordDebug({ ably: { ...lightShowDebug.ably, clientId } });

    import("ably")
      .then(({ Realtime }) => {
        if (cancelled) return;
        client = new Realtime({
          clientId,
          authUrl: `/ably/token?${new URLSearchParams({ eventId, clientId })}`,
          // Detailed debug output (connection, channel and transport events,
          // and each message received) to the console, which LogRocket keeps
          // for the sessions it records; with ?lightshowdebug, to the
          // overlay too.
          logLevel: 4,
          ...(isLightShowDebug && { logHandler: recordAblyLog }),
        });
        client.connection.on((stateChange) =>
          recordDebug({
            ably: {
              ...lightShowDebug.ably,
              state: stateChange.current,
              ...(stateChange.reason && {
                lastError: `${stateChange.reason.code} ${stateChange.reason.message}`,
              }),
            },
          }),
        );
        const channel = client.channels.get(`lightshow:${eventId}`, {
          params: { rewind: "1" },
        });
        channel.on((stateChange) =>
          recordDebug({
            ably: {
              ...lightShowDebug.ably,
              channel: stateChange.current,
              ...(stateChange.reason && {
                lastError: `${stateChange.reason.code} ${stateChange.reason.message}`,
              }),
            },
          }),
        );
        channel.subscribe("live", (message: InboundMessage) => {
          if (!isLiveMessage(message.data)) return;
          const { look, startDelayMs } = message.data;
          const startedAtMs = message.data.startedAtMs ?? message.timestamp;
          if (typeof startedAtMs !== "number") return;
          // A reconnect can replay the last message; only a newer change
          // replaces the current one.
          setChange((current) =>
            current && startedAtMs <= current.startedAtMs
              ? current
              : { look, startedAtMs, startDelayMs },
          );
          const arrival = {
            lookName: look?.name,
            startedAtMs,
            startAtMs: startedAtMs + startDelayMs,
            receivedAtMs: Date.now() + clockOffsetRef.current,
          };
          recordArrival({ ...arrival, source: "ably", localMs: Date.now() });
          recordDebug({
            ably: {
              ...lightShowDebug.ably,
              messages: (lightShowDebug.ably?.messages ?? 0) + 1,
              lastMessage: arrival,
            },
          });
        });
      })
      .catch(() => recordDebug({ ably: { state: "failed to load" } }));

    return () => {
      cancelled = true;
      client?.close();
    };
  }, [eventId, enabled, clientId]);

  return change;
};
